// file-drop.directive.ts
// Ported from customer-website's app-file-drop directive so both apps' upload
// fields share the same drag-and-drop behavior/markup contract.
import { Directive, EventEmitter, HostBinding, HostListener, Input, Output } from '@angular/core';

@Directive({
  selector: '[appFileDrop]',
  standalone: true,
})
export class FileDropDirective {
  @Input() accept = '';                 // e.g. ".png,.jpg,.jpeg,.pdf" or "image/*"
  @Input() disabled = false;

  @Output() filesDropped = new EventEmitter<File[]>();
  @Output() invalidDrop = new EventEmitter<File[]>(); // (optional) invalid types

  @HostBinding('class.is-dragover') _dragOver = false;

  @HostListener('dragover', ['$event'])
  onDragOver(evt: DragEvent) {
    if (this.disabled) return;
    evt.preventDefault();
    evt.stopPropagation();
    this._dragOver = true;
  }

  @HostListener('dragenter', ['$event'])
  onDragEnter(evt: DragEvent) {
    if (this.disabled) return;
    evt.preventDefault();
    evt.stopPropagation();
    this._dragOver = true;
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(evt: DragEvent) {
    if (this.disabled) return;
    evt.preventDefault();
    evt.stopPropagation();
    // Only clear when actually leaving the host
    if ((evt.target as HTMLElement) === evt.currentTarget) this._dragOver = false;
  }

  @HostListener('drop', ['$event'])
  onDrop(evt: DragEvent) {
    if (this.disabled) return;
    evt.preventDefault();
    evt.stopPropagation();
    this._dragOver = false;

    const dtFiles = Array.from(evt.dataTransfer?.files ?? []);
    if (!dtFiles.length) return;

    const valid: File[] = [];
    const invalid: File[] = [];

    for (const f of dtFiles) (this.matchesAccept(f) ? valid : invalid).push(f);

    if (valid.length) this.filesDropped.emit(valid);
    if (invalid.length) this.invalidDrop.emit(invalid);
  }

  private matchesAccept(file: File): boolean {
    if (!this.accept) return true;
    const patterns = this.accept.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);

    const ext = '.' + (file.name.split('.').pop() ?? '').toLowerCase();
    const mime = file.type.toLowerCase();

    return patterns.some(p => {
      if (p.startsWith('.')) return p === ext;
      if (p.endsWith('/*')) return mime.startsWith(p.slice(0, -1)); // e.g. image/*
      return mime === p;
    });
  }
}
